import { useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'

import { Drawer, Layout, Tabs, Tag, Text } from '@loadsmart/miranda-react'


import { RequestStatus } from 'core/domain/Request'
import { formatMode } from 'core/presenters/modePresenter'
import { LoadingState } from 'core/ui/async-content/LoadingState'

import { useConfigContext } from 'config/ui/ConfigContext'

import { useShipperDetail } from '../../hooks/useShipperDetail'
import {
  formatShipperAddress,
  formatShipperLocation,
  mapShipperCountryToCountryFlag,
} from '../../presenters/shipperPresenter'
import { ShipperDetailsTab } from './shipper-details-tab/ShipperDetailsTab'
import { ShipperDocumentsTab } from './shipper-documents-tab/ShipperDocumentsTab'

export const SHIPPER_DETAIL_DRAWER_TOAST_ID = 'shipper-detail-drawer-toast-id'

export type ShipperDetailDrawerProps = {
  shipperUUID: string | undefined
  isOpen: boolean
  onClose: () => void
}

type ShipperDetailTab = 'details' | 'documents'

export function ShipperDetailDrawer({ shipperUUID, isOpen, onClose }: ShipperDetailDrawerProps) {
  const { shipperDetail, status } = useShipperDetail(shipperUUID)
  const [activeTab, setActiveTab] = useState<ShipperDetailTab>('details')
  const { enableUploadShipperDocuments } = useConfigContext()
  const showDocumentsTab = enableUploadShipperDocuments.toBoolean()

  const isLoading = status === RequestStatus.Pending || status === RequestStatus.Initial

  useEffect(() => {
    if (status === RequestStatus.Error) {
      onClose()
      toast.error('Failed to load shipper details', { containerId: SHIPPER_DETAIL_DRAWER_TOAST_ID })
    }
  }, [status, onClose])

  if (!isOpen) return null

  return (
    <Drawer
      open={isOpen}
      onClose={(event: CustomEvent) => {
        if (event.target === event.currentTarget) return
        onClose()
      }}
      size="medium"
    >
      <Drawer.Header>
        <Text variant="heading-md-bold">Shipper</Text>
        <Drawer.Close />
      </Drawer.Header>

      <Drawer.Body>
        <ToastContainer containerId={SHIPPER_DETAIL_DRAWER_TOAST_ID} />
        {isLoading && (
          <Layout.Box padding="spacing-6">
            <LoadingState />
          </Layout.Box>
        )}

        {status === RequestStatus.Done && shipperDetail && (
          <Layout.Stack gap="spacing-4">
            <Layout.Box
              backgroundColor="color-background-primary"
              borderColor="color-border"
              borderWidth="border-thin"
              borderRadius="border-radius-s"
              padding="spacing-4"
            >
              <Layout.Stack gap="spacing-3">
                <Text variant="heading-md-bold">{shipperDetail.companyName}</Text>
                <Layout.Stack gap="spacing-1">
                  <Text variant="body-md" color="color-text-secondary">
                    {formatShipperAddress(shipperDetail)}
                  </Text>
                  <Layout.Group gap="spacing-2" align="center">
                    <Text variant="body-md" color="color-text-secondary">
                      {formatShipperLocation(shipperDetail)}
                    </Text>
                    {shipperDetail.country && (
                      <Layout.Group gap="spacing-1" align="center">
                        {mapShipperCountryToCountryFlag(shipperDetail.country)}
                        <Text variant="body-md" color="color-text-secondary">
                          {shipperDetail.country}
                        </Text>
                      </Layout.Group>
                    )}
                  </Layout.Group>
                </Layout.Stack>
                {shipperDetail.modes.length > 0 && (
                  <Layout.Group gap="spacing-2">
                    {shipperDetail.modes.map(mode => (
                      <Tag key={mode} size="small" variant="neutral">
                        {formatMode(mode)}
                      </Tag>
                    ))}
                  </Layout.Group>
                )}
              </Layout.Stack>
            </Layout.Box>

            <Layout.Box
              backgroundColor="color-background-primary"
              borderColor="color-border"
              borderWidth="border-thin"
              borderRadius="border-radius-s"
              padding="spacing-4"
            >
              <Layout.Stack gap="spacing-3">
                <Text variant="heading-sm-bold">Overview</Text>
                <Tabs
                  activeTab={activeTab}
                  onTabChange={event => {
                    setActiveTab(event.detail.value as ShipperDetailTab)
                  }}
                >
                  <ShipperDetailsTab details={shipperDetail} />
                  {showDocumentsTab && <ShipperDocumentsTab details={shipperDetail} />}
                </Tabs>
              </Layout.Stack>
            </Layout.Box>
          </Layout.Stack>
        )}
      </Drawer.Body>
    </Drawer>
  )
}
